import React from 'react';
import styled from 'styled-components';
import VisualCircle from './VisualCircle';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { Investment } from '../api';
import { DonationVisualProps } from './utils';
import { numberWithCommas } from 'shared/utils/numberUtils';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { COLORS } from 'shared/design-system/settings/variables/Colors';

interface DonationDetailsVisualProps {
  width?: number;
  height?: number;
  className?: string;
  donation: Investment;
  unallocatedAmount: number;
  selectedIndex: number | undefined;
  calculatedTotalDonations: number;
  middleComponent?: React.ReactNode;
  donationList: DonationVisualProps[];
  setSelectedIndex: React.Dispatch<React.SetStateAction<number | undefined>>;
}

const StyledDiv = styled.div`
  position: relative;
  text-align: center;
  path {
    cursor: pointer;
  }
  .visual-label {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    .donation-label {
      margin-bottom: ${SPACERS.tiny};
    }
  }
`;

const DonationDetailsVisual: React.FC<DonationDetailsVisualProps> = ({
  width,
  height,
  donationList,
  middleComponent,
  calculatedTotalDonations,
  selectedIndex,
  donation,
  unallocatedAmount,
  className,
  setSelectedIndex,
}) => {
  const totalDonation = donation.donation || 0;
  const selectedAmount =
    selectedIndex === undefined
      ? 0
      : donation.allocations?.[selectedIndex].amount || 0;
  const circleDivisions = [
    ...donationList.map(d => ({
      color: d.color,
      percentage: totalDonation > 0 ? (d.amount / totalDonation) * 100 : 0,
    })),
    ...(unallocatedAmount > 0
      ? [
          {
            color: COLORS.blackPure,
            percentage:
              totalDonation > 0 ? (unallocatedAmount / totalDonation) * 100 : 0,
          },
        ]
      : []),
  ];
  return (
    <StyledDiv className={className}>
      <div className="visual-label">
        {middleComponent || (
          <>
            <TrueImpactText
              variant="heading-label"
              color="blackBase"
              className="donation-label"
            >
              {selectedIndex === undefined
                ? 'TOTAL DONATION'
                : `${((selectedAmount / totalDonation) * 100).toFixed(
                    2,
                  )}% OF DONATION`}
            </TrueImpactText>
            <TrueImpactText variant="body-large" color="blackBase">
              {selectedIndex === undefined
                ? `$${numberWithCommas(donation.donation)}`
                : `$${numberWithCommas(selectedAmount)}`}
            </TrueImpactText>
          </>
        )}
      </div>
      <VisualCircle
        width={width}
        height={height}
        selectedIndex={selectedIndex}
        handleClick={index => {
          if (index === undefined) {
            setSelectedIndex(undefined);
            return;
          }
          if (index >= donationList.length) {
            setSelectedIndex(undefined);
            return;
          }
          setSelectedIndex(index);
        }}
        divisions={circleDivisions}
      />
    </StyledDiv>
  );
};

export default DonationDetailsVisual;
